import { Avatar, Box, HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { LuScissors } from 'react-icons/lu'

import { getBoardDropShadow, tv, TV_BOARD_FRAME } from '../model/board-layout'
import type { BoardAppointment, BoardStation } from '../model/board-types'
import {
    formatAppointmentLabel,
    getCurrentClientCardView,
    getStationHaircutsLabel,
    getStationSeatLabel,
} from '../model/queue-view'

interface CurrentClientCardProps {
    view: ReturnType<typeof getCurrentClientCardView>
}

interface BoardMasterCardProps {
    station: BoardStation
}

const TILE_SHADOW = `0 0 ${tv(3)} rgba(24,24,27,0.3), 0 ${tv(12)} ${tv(22)} rgba(24,24,27,0.1)`
const BOARD_MASTER_CARD_BACKGROUND =
    'linear-gradient(to bottom, #B8D3D6 0%, #E7F0F1 28%, #FFFFFF 46%)'
const BOARD_MASTER_CARD_PAUSED_BACKGROUND = '#ffe4e7'
const QUEUE_TILE_SHADOW = getBoardDropShadow(TV_BOARD_FRAME.queueTile.shadow)
const WAITING_CLIENT_CARD_PULSE_DURATION = '1.25s'
const WAITING_CLIENT_CARD_GLOW = `0 0 ${tv(18)} rgba(78,198,219,0.72), 0 ${tv(18)} ${tv(
    34
)} rgba(36,126,143,0.24)`
const WAITING_CLIENT_CARD_PULSE = keyframes`
    0%, 100% {
        background-color: #eafffb;
        border-color: #8ee8f2;
        box-shadow: ${TILE_SHADOW};
        transform: scale(1);
    }

    45% {
        background-color: #bff4ff;
        border-color: #37bdd3;
        box-shadow: ${WAITING_CLIENT_CARD_GLOW};
        transform: scale(1.025);
    }

    70% {
        background-color: #d8fbff;
        border-color: #62d8e8;
        box-shadow: ${WAITING_CLIENT_CARD_GLOW};
        transform: scale(1.01);
    }
`

const CURRENT_CLIENT_CARD_STYLES = {
    active: {
        bg: '#4e9aaa',
        borderColor: '#4e9aaa',
        captionColor: '#fffefa',
        labelColor: '#fffefa',
        labelFontSize: 112,
        labelFontWeight: '900',
    },
    called: {
        bg: '#f4f8f9',
        borderColor: '#e6f0f3',
        captionColor: '#428696',
        labelColor: '#428696',
        labelFontSize: 112,
        labelFontWeight: '900',
    },
    waiting: {
        bg: '#eafffb',
        borderColor: '#8ee8f2',
        captionColor: '#247e8f',
        labelColor: '#247e8f',
        labelFontSize: 112,
        labelFontWeight: '900',
    },
    empty: {
        bg: '#f4f8f9',
        borderColor: '#e4e4e7',
        captionColor: '#376f7c',
        labelColor: '#52525b',
        labelFontSize: 108,
        labelFontWeight: '900',
    },
    paused: {
        bg: '#f4f8f9',
        borderColor: '#e6f0f3',
        captionColor: '#376f7c',
        labelColor: '#a1a1aa',
        labelFontSize: 48,
        labelFontWeight: '700',
    },
} as const

const CurrentClientCard = ({ view }: CurrentClientCardProps) => {
    const styles = CURRENT_CLIENT_CARD_STYLES[view.tone]
    const isWaitingForClient = view.tone === 'waiting'
    const shouldWrapWaitingLabel = isWaitingForClient && view.isFallbackLabel
    const labelFontSize = shouldWrapWaitingLabel ? 52 : styles.labelFontSize
    const waitingClientAnimation = isWaitingForClient
        ? `${WAITING_CLIENT_CARD_PULSE} ${WAITING_CLIENT_CARD_PULSE_DURATION} ease-in-out infinite`
        : undefined
    const waitingClientWillChange = isWaitingForClient
        ? 'transform, background-color, border-color'
        : undefined

    return (
        <Box
            alignItems='center'
            animation={waitingClientAnimation}
            bg={styles.bg}
            border={`${tv(3)} solid ${styles.borderColor}`}
            borderRadius={tv(48)}
            boxShadow={TILE_SHADOW}
            display='flex'
            flexDir='column'
            flexShrink={0}
            gap={tv(10)}
            h={tv(280)}
            justifyContent='center'
            overflow='hidden'
            position='relative'
            px={tv(32)}
            py={tv(30)}
            transformOrigin='center'
            w='full'
            willChange={waitingClientWillChange}
        >
            {view.caption && (
                <Text
                    color={styles.captionColor}
                    fontSize={tv(36)}
                    fontWeight='700'
                    lineHeight='1.3'
                    position='relative'
                    whiteSpace='nowrap'
                >
                    {view.caption}
                </Text>
            )}
            <Text
                color={styles.labelColor}
                fontSize={tv(labelFontSize)}
                fontWeight={styles.labelFontWeight}
                lineHeight='1.05'
                maxW='full'
                overflow='hidden'
                position='relative'
                textAlign={isWaitingForClient ? 'center' : undefined}
                textOverflow='ellipsis'
                w={isWaitingForClient ? 'full' : undefined}
                whiteSpace={shouldWrapWaitingLabel ? 'normal' : 'nowrap'}
            >
                {view.label}
            </Text>
        </Box>
    )
}

const QueueTile = ({ appointment }: { appointment: BoardAppointment }) => {
    return (
        <Box
            alignItems='center'
            bg='#ffffff'
            borderRadius={tv(48)}
            boxShadow={QUEUE_TILE_SHADOW}
            display='flex'
            flexShrink={0}
            h={tv(200)}
            justifyContent='center'
            px={tv(64)}
            w='full'
        >
            <Text
                color='#212121'
                fontSize={tv(96)}
                fontWeight='900'
                lineHeight='1.16'
                whiteSpace='nowrap'
            >
                {formatAppointmentLabel(appointment)}
            </Text>
        </Box>
    )
}

const HiddenQueueTile = ({ count }: { count: number }) => {
    return (
        <Box
            alignItems='center'
            bg='#f6f8f7'
            borderRadius={tv(48)}
            display='flex'
            flexShrink={0}
            h={tv(112)}
            justifyContent='center'
            w='full'
        >
            <Text color='#647a74' fontSize={tv(56)} fontWeight='800' whiteSpace='nowrap'>
                +{count}
            </Text>
        </Box>
    )
}

export const BoardMasterCard = ({ station }: BoardMasterCardProps) => {
    const queue = station.queue ?? []
    const visibleQueue = queue.slice(0, 4)
    const hiddenQueueCount = Math.max(queue.length - visibleQueue.length, 0)
    const haircutsLabel = getStationHaircutsLabel(station)
    const currentClientView = getCurrentClientCardView(station)
    const cardBackground =
        currentClientView.tone === 'paused'
            ? BOARD_MASTER_CARD_PAUSED_BACKGROUND
            : BOARD_MASTER_CARD_BACKGROUND

    return (
        <Box
            bg={cardBackground}
            border={`${tv(3)} solid #ffffff`}
            borderRadius={tv(72)}
            clipPath='inset(0 round inherit)'
            display='flex'
            flexDir='column'
            h={tv(TV_BOARD_FRAME.masters.cardHeight)}
            minW={tv(TV_BOARD_FRAME.masters.cardWidth)}
            overflow='hidden'
            p={tv(24)}
            position='relative'
            w={tv(TV_BOARD_FRAME.masters.cardWidth)}
        >
            <VStack alignItems='center' gap={tv(56)} h='full' minH={0} position='relative' w='full'>
                <VStack
                    alignItems='center'
                    flexShrink={0}
                    gap={tv(40)}
                    h={tv(952)}
                    position='relative'
                    w='full'
                >
                    <VStack alignItems='center' gap={tv(16)} maxW='full'>
                        <Avatar.Root flexShrink={0} h={tv(240)} shape='full' w={tv(240)}>
                            <Avatar.Fallback name={station.barber_name ?? 'Мастер'} />
                            <Avatar.Image src={station.barber_photo_url} />
                        </Avatar.Root>

                        <Text
                            color='#212121'
                            fontSize={tv(60)}
                            fontWeight='700'
                            lineHeight='1.2'
                            maxW='full'
                            overflow='hidden'
                            textOverflow='ellipsis'
                            whiteSpace='nowrap'
                        >
                            {station.barber_name ?? 'Мастер'}
                        </Text>

                        <HStack
                            bg='#ffffff'
                            borderRadius='full'
                            boxShadow={TILE_SHADOW}
                            flexShrink={0}
                            gap={tv(14)}
                            h={tv(96)}
                            justifyContent='center'
                            maxW='full'
                            px={tv(34)}
                        >
                            <Icon as={LuScissors} boxSize={tv(55)} color='#52525b' />
                            <Text
                                color='#52525b'
                                fontSize={tv(48)}
                                fontWeight='700'
                                lineHeight='1.42'
                                overflow='hidden'
                                textOverflow='ellipsis'
                                whiteSpace='nowrap'
                            >
                                {haircutsLabel}
                            </Text>
                        </HStack>
                    </VStack>

                    <Box
                        alignItems='center'
                        bg='#ffffff'
                        border={`${tv(3)} solid #ffffff`}
                        borderRadius={tv(48)}
                        boxShadow={TILE_SHADOW}
                        display='flex'
                        flexShrink={0}
                        h={tv(152)}
                        justifyContent='center'
                        px={tv(47)}
                        w='full'
                    >
                        <Text
                            color='#71717a'
                            fontSize={tv(100)}
                            fontWeight='900'
                            lineHeight='0.94'
                            overflow='hidden'
                            textOverflow='ellipsis'
                            whiteSpace='nowrap'
                        >
                            {getStationSeatLabel(station)}
                        </Text>
                    </Box>

                    <CurrentClientCard view={currentClientView} />
                </VStack>

                <VStack alignItems='center' flex={1} gap={tv(24)} minH={0} w='full'>
                    <Text
                        color='#a1a1aa'
                        fontSize={tv(36)}
                        fontWeight='700'
                        lineHeight='1.22'
                        whiteSpace='nowrap'
                    >
                        Очередь
                    </Text>

                    {visibleQueue.length > 0 ? (
                        <VStack alignItems='stretch' gap={tv(16)} overflow='visible' w='full'>
                            {visibleQueue.map((appointment, index) => (
                                <QueueTile
                                    key={
                                        appointment.id ??
                                        appointment.appointment_id ??
                                        appointment.queue_position ??
                                        index
                                    }
                                    appointment={appointment}
                                />
                            ))}

                            {hiddenQueueCount > 0 && <HiddenQueueTile count={hiddenQueueCount} />}
                        </VStack>
                    ) : (
                        <Box alignItems='center' display='flex' flex={1} justifyContent='center'>
                            <Text color='#a1a1aa' fontSize={tv(44)} fontWeight='700'>
                                Ким да суох :(
                            </Text>
                        </Box>
                    )}
                </VStack>
            </VStack>
        </Box>
    )
}
